import { forwardRef, useEffect, useState } from 'react';

import { getCheckoutLineUnitPriceWithoutDiscount } from '@/utils/checkout';

import { Complimentary } from '@/components/Complimentary/Complimentary';
import { Flex } from '@/components/Flex';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader } from '@/components/Loader';
import { SalesTaxBadge } from '@/components/SalesTaxBadge';
import type {
  CheckoutLineFragment,
  Product,
  TaxedMoneyFragment,
} from '@/graphql/saleor/api';
import {
  useProductVariantStrapDetailsQuery,
  useStrapSizesQuery,
} from '@/graphql/saleor/api';
import { getMultiselectValue } from '@/lib/attributes';
import { getLineSubheader } from '@/lib/checkout';
import { STRAP_SIZES_FETCH_LIMIT, VOUCHER_TYPE } from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { sumPrices } from '@/lib/prices';
import {
  getImageScaleRatio,
  getProductType,
  getTranslatedProductName,
} from '@/lib/product';
import type { Maybe, ShoppingBagProductWithCheckoutLineId } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { CheckoutState } from '@/states/checkout';
import { WarrantyImage } from '@/views/ShoppingBagView/components/ShoppingBagCard/components/WarrantyImage/WarrantyImage';
import { StrapSelect } from '@/views/ShoppingBagView/components/StrapSelect';

import * as M from './messages';
import * as S from './ShoppingBagItem.styles';

export interface ShoppingBagItemProps
  extends Partial<Pick<CheckoutState, 'preOrderData'>> {
  channel?: string;
  hasBraceletSizeNotSelectedError: boolean;
  hasExtendWarranty?: boolean;
  isDisabled?: boolean;
  isProcessing?: boolean;
  lineId: string;
  onDelete: (lineId: string) => void;
  onDeleteBuckle?: () => void;
  price: Maybe<TaxedMoneyFragment>;
  productVariant: ShoppingBagProductWithCheckoutLineId;
  relatedBuckle: Maybe<CheckoutLineFragment>;
  showBuckleSizeSelector: boolean;
  strapPack?: boolean;
  voucherType?: string;
  warrantyExtensionDuration?: string;
}

export const ShoppingBagItem = forwardRef<HTMLElement, ShoppingBagItemProps>(
  (
    {
      channel,
      hasBraceletSizeNotSelectedError,
      isDisabled = false,
      isProcessing = false,
      lineId,
      onDelete,
      onDeleteBuckle,
      preOrderData,
      price,
      productVariant,
      relatedBuckle,
      showBuckleSizeSelector,
      strapPack = false,
      voucherType,
      warrantyExtensionDuration,
    },
    ref,
  ) => {
    const [otherSizesSkus, setOtherSizesSkus] = useState<string[]>([]);
    const [sku, setSku] = useState<string>('');
    const {
      id: productVariantId,
      product,
      sku: productVariantSku,
    } = productVariant;

    const {
      query: { channel: channelRegionProvider, languageCode },
    } = useRegionProvider();
    const { refetch: fetchVariantDetails } = useProductVariantStrapDetailsQuery(
      {
        variables: {
          channel: channelRegionProvider,
          languageCode,
          sku: productVariantSku!,
        },
        skip: true,
      },
    );
    const { refetch: fetchStrapSizes } = useStrapSizesQuery({
      variables: {
        channel: channelRegionProvider,
        languageCode,
        limit: STRAP_SIZES_FETCH_LIMIT,
        filter: { sku: [productVariantSku!, ...otherSizesSkus] },
      },
      skip: true,
    });
    useEffect(() => {
      const fetchDetails = async () => {
        const { data } = await fetchVariantDetails();
        setOtherSizesSkus(
          getMultiselectValue(data.productVariant?.product.otherSizesSkus),
        );
      };

      fetchDetails();
    }, [fetchVariantDetails]);

    useEffect(() => {
      const fetchDetails = async () => {
        const { data: strapSizesData } = await fetchStrapSizes();

        const products = mapEdgesToItems(strapSizesData.productVariants);
        const foundVariant = products.find(
          (product: { id: string }) => product.id === productVariantId,
        );
        setSku(foundVariant?.sku || '');
      };

      fetchDetails();
    }, [otherSizesSkus]);

    const { productType, thumbnail } = product;

    const productName = getTranslatedProductName(product);
    const relatedUnitPriceWithoutDiscount = relatedBuckle
      ? getCheckoutLineUnitPriceWithoutDiscount(relatedBuckle, voucherType)
      : undefined;
    const productPrice =
      sumPrices(price, relatedUnitPriceWithoutDiscount) ?? price;
    const isWatch = getProductType(productType) === 'watch';
    const isStrap = getProductType(productType) === 'strap';
    const isTool = getProductType(productType) === 'tool';
    const productTypeName = getProductType(productType);
    const isWarranty = productTypeName === 'warranty';

    return (
      <S.ItemWrapper ref={ref} data-test-id='ShoppingBagItem'>
        <S.WatchBox data-test-id='ShoppingBagItemWatchBox'>
          <Flex position='relative'>
            {(!strapPack || isTool) && (
              <S.DeleteButton
                data-testid='cartRemove'
                disabled={isDisabled}
                onClick={() => onDelete(productVariant.checkoutLineId)}
              >
                {isProcessing ? (
                  <Loader
                    data-test-id='ShoppingBagItemLoader'
                    height={35}
                    noMargin
                    size='sm'
                  />
                ) : (
                  <S.DeleteIcon />
                )}
              </S.DeleteButton>
            )}
            {warrantyExtensionDuration && productTypeName === 'warranty' ? (
              <WarrantyImage
                data-test-id='ShoppingBagItemWarrantyImage'
                height='96px'
                warrantyExtensionDuration={warrantyExtensionDuration}
                width='96px'
              />
            ) : (
              <S.ImageWrapper data-test-id='ShoppingBagItemImageWrapper'>
                {thumbnail ? (
                  <S.Image
                    $scaleRatio={getImageScaleRatio(productType)}
                    alt={thumbnail?.alt ?? ''}
                    data-test-id='ShoppingBagItemThumbnailImage'
                    height={0}
                    productType={productTypeName}
                    sizes='20vw'
                    src={thumbnail?.url ?? ''}
                    width={0}
                  />
                ) : (
                  <ImagePlaceholder productType={productTypeName} />
                )}
              </S.ImageWrapper>
            )}
            <S.BuckleTreeView>
              <S.RowContent data-test-id='ShoppingBagItemRowContent'>
                <Flex
                  flexDirection='column'
                  gridGap='s025'
                  justifyContent='end'
                >
                  <S.Name data-test-id='ShoppingBagItemProductName'>
                    {isWarranty && warrantyExtensionDuration
                      ? `${productName} (${warrantyExtensionDuration})`
                      : productName}
                  </S.Name>
                  <S.AdditionalName
                    data-test-id='ShoppingBagItemLineSubheader'
                    scale='p2'
                  >
                    {getLineSubheader(product, sku)}
                  </S.AdditionalName>
                  {isWatch && showBuckleSizeSelector && (
                    <StrapSelect
                      className='strap-size-select'
                      data-test-id='ShoppingBagItemStrapSelect'
                      hasError={hasBraceletSizeNotSelectedError}
                      line={product as Product}
                      lineId={lineId}
                    />
                  )}
                  {productTypeName === 'strap-pack' && (
                    <Flex alignItems='end' flexDirection='column'>
                      <>
                        <S.Price
                          channel={channel}
                          data-test-id='ShoppingBagItemPrice'
                          displayZero
                          justifyContent='end'
                          price={productPrice}
                          size='inherited'
                        />
                        <SalesTaxBadge
                          channel={channel}
                          data-test-id='ShoppingBagItemSalesTaxBadge'
                        />
                      </>
                    </Flex>
                  )}
                </Flex>
              </S.RowContent>
              {isStrap && relatedBuckle && (
                <S.BuckleWrapper gridGap='s100' position='relative'>
                  <S.BuckleImageWrapper>
                    {relatedBuckle.variant.product.thumbnail ? (
                      <S.Image
                        $scaleRatio={getImageScaleRatio(productType)}
                        alt={relatedBuckle.variant.product.thumbnail?.alt ?? ''}
                        data-test-id='ShoppingBagItemRelatedBuckleImage'
                        fill
                        sizes='20vw'
                        src={relatedBuckle.variant.product.thumbnail?.url ?? ''}
                      />
                    ) : (
                      <ImagePlaceholder productType='buckle' />
                    )}
                  </S.BuckleImageWrapper>
                  <Flex flexDirection='column'>
                    <S.BuckleName>
                      {getTranslatedProductName(relatedBuckle.variant.product)}
                    </S.BuckleName>
                    <S.AdditionalName scale='p3'>
                      {relatedBuckle.variant.sku}
                    </S.AdditionalName>
                  </Flex>
                  <S.DeleteButton
                    data-testid='cartRemove'
                    disabled={isDisabled}
                    isBuckle
                    onClick={onDeleteBuckle}
                  >
                    {isProcessing ? (
                      <Loader
                        data-test-id='ShoppingBagItemBuckleLoader'
                        height={35}
                        noMargin
                        size='sm'
                      />
                    ) : (
                      <S.DeleteIcon />
                    )}
                  </S.DeleteButton>
                </S.BuckleWrapper>
              )}
            </S.BuckleTreeView>
          </Flex>
          {productTypeName !== 'strap-pack' && (
            <Flex alignItems='end' flexDirection='column'>
              {productTypeName === 'gift' ? (
                <Complimentary />
              ) : (
                <>
                  <S.Price
                    channel={channel}
                    data-test-id='ShoppingBagItemPrice'
                    displayZero={voucherType !== VOUCHER_TYPE.PERCENTAGE}
                    justifyContent='end'
                    price={productPrice}
                    size='inherited'
                  />
                  {voucherType !== VOUCHER_TYPE.PERCENTAGE && (
                    <SalesTaxBadge
                      channel={channel}
                      data-test-id='ShoppingBagItemSalesTaxBadge'
                    />
                  )}
                </>
              )}
            </Flex>
          )}
        </S.WatchBox>
        {preOrderData && (
          <S.PreOrderNote data-test-id='ShoppingBagItemPreOrderNote'>
            {M.PreOrderNote}
          </S.PreOrderNote>
        )}
      </S.ItemWrapper>
    );
  },
);

ShoppingBagItem.displayName = 'ShoppingBagItem';
